/**
 * THE ROCKET PACK — five rockets, no clock, spend them whenever you like.
 *
 * The player's ask, verbatim: "we should also add a rockets power up that is just 5 rockets that
 * is not timed and they can use anytime."
 *
 * **IT IS THE ROCKET'S SPEC WITH A DIFFERENT USE MODEL, AND THAT IS THE ENTIRE FILE.** Everything
 * about what a rocket *does* — the speed, the range, the two bounds, the radius, the muzzle
 * clearance, the shatter it reuses off the spike — is imported from `./rocket.js` rather than
 * restated, so the two entries can never drift into being two subtly different rockets. What is
 * declared here is only the half the registry exists to vary: `use`.
 *
 * **WHY IT IS A SECOND ENTRY RATHER THAN A SETTING ON THE FIRST.** The simulation branches on
 * `UseModel.model` and never on an id, so "timed" and "charged" cannot be two states of one
 * power-up without something, somewhere, asking which rocket it is holding. Two registry entries
 * is the shape this module was built for: a new file and one line in `POWER_UPS`.
 *
 * **THE TWO ARE DIFFERENT KINDS TO AN INVENTORY, WHICH IS THE POINT.** A pickup tops up a slot of
 * the *same* kind, so a pack does not refill a window and a window does not refill a pack, and a
 * player may carry both at once and switch between them — a stockpile they are saving and a
 * window they are about to spend, which is exactly the choice three slots were widened for.
 *
 * The two read apart at a glance because a charged slot draws its count (`x5`) in the orb's gold
 * where a running window draws seconds in amber; the glyph is deliberately a different rocket
 * rather than the same one, so the *chip* is legible before the counter under it is.
 */

import { PROJECTILE_MUZZLE_CLEARANCE } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';
import {
  ROCKET_LIFETIME_TICKS,
  ROCKET_RADIUS,
  ROCKET_RANGE,
  ROCKET_SPEED,
  rocketPowerUp,
} from './rocket.js';

export const POWER_UP_ROCKET_PACK = 'rocket_pack';

/**
 * Firings one pack is worth. **Five**, and it is the player's own number.
 *
 * It is worth about a fifth of what a spent rocket window is (`ROCKET_DURATION_TICKS` over
 * `ROCKET_FIRE_COOLDOWN_TICKS` is roughly twenty-five), and that is the trade rather than an
 * imbalance: a window is worth its twenty-five only to somebody who has a target in front of them
 * for the whole ten seconds, and this is worth its five whenever they turn up. A stockpile that
 * waits is allowed to be smaller than a window that does not.
 */
export const ROCKET_PACK_CHARGES = 5;

/**
 * The gap between two of them, in ticks — **the window's own `ROCKET_FIRE_COOLDOWN_TICKS`**.
 *
 * Taken from the other entry rather than chosen, because the reason for that number is a fact
 * about a rocket in flight and not about how many you are holding: it is a little under a third of
 * `ROCKET_LIFETIME_TICKS`, so a firer holding the button keeps three or four of their own in the
 * air. Five presses on five consecutive ticks would be five rockets in a quarter of a second,
 * which is what a charged entry needs a cooldown for at all.
 */
export const ROCKET_PACK_FIRE_COOLDOWN_TICKS = rocketPowerUp.use.cooldownTicks;

export const rocketPackPowerUp: PowerUp = {
  id: POWER_UP_ROCKET_PACK,
  name: 'Rocket Pack',
  description: `${ROCKET_PACK_CHARGES} rockets to spend whenever you like. No timer.`,
  glyph: '🧨',
  use: {
    model: 'charged',
    charges: ROCKET_PACK_CHARGES,
    cooldownTicks: ROCKET_PACK_FIRE_COOLDOWN_TICKS,
  },
  // Every number here is the rocket's own, imported rather than restated: one rocket, two ways of
  // being carried. See the note at the top of this file.
  projectile: {
    motion: 'launched',
    effect: 'shatter',
    speed: ROCKET_SPEED,
    range: ROCKET_RANGE,
    lifetimeTicks: ROCKET_LIFETIME_TICKS,
    armingTicks: 0,
    radius: ROCKET_RADIUS,
    standoff: PROJECTILE_MUZZLE_CLEARANCE,
    hitsOwner: false,
  },
};
